import { useCallback, useEffect, useRef, useState } from 'react';
import { useAdaptivePolling } from './useAdaptivePolling';
import { queryPrometheusInstantBatch } from '../services/grafana';

export interface ProbeRegionStatus {
  region: string;
  regionLabel: string;
  success: boolean;
  statusCode: number | null;
  durationMs: number | null;
  /** Error type from probe_http_error_type gauge: timeout | dns | connection | http_error | unknown | null */
  errorType: string | null;
  target: string;
  checkedAt: string;
}

/** All probe results for a single target URL, broken down by region. */
export interface ProbeTargetStatus {
  target: string;
  regions: ProbeRegionStatus[];
  upCount: number;
  totalCount: number;
  avgMs: number | null;
}

export const REGION_LABELS: Record<string, string> = {
  // US
  'us-east-1':      'N. Virginia',
  'us-east-2':      'Ohio',
  'us-west-1':      'N. California',
  'us-west-2':      'Oregon',
  // Canada
  'ca-central-1':   'Canada (Montreal)',
  'ca-west-1':      'Canada (Calgary)',
  // Europe
  'eu-west-1':      'Ireland',
  'eu-west-2':      'London',
  'eu-west-3':      'Paris',
  'eu-central-1':   'Frankfurt',
  'eu-central-2':   'Zurich',
  'eu-north-1':     'Stockholm',
  'eu-south-1':     'Milan',
  'eu-south-2':     'Spain',
  // Asia Pacific
  'ap-northeast-1': 'Tokyo',
  'ap-northeast-2': 'Seoul',
  'ap-northeast-3': 'Osaka',
  'ap-southeast-1': 'Singapore',
  'ap-southeast-2': 'Sydney',
  'ap-southeast-3': 'Jakarta',
  'ap-southeast-4': 'Melbourne',
  'ap-south-1':     'Mumbai',
  'ap-south-2':     'Hyderabad',
  'ap-east-1':      'Hong Kong',
  // South America
  'sa-east-1':      'São Paulo',
  // Middle East
  'me-south-1':     'Bahrain',
  'me-central-1':   'UAE',
  // Africa
  'af-south-1':     'Cape Town',
  // Israel
  'il-central-1':   'Tel Aviv',
};

/** Maps probe_http_error_type integer codes back to string names. */
const ERROR_TYPE_BY_CODE: Record<number, string | null> = {
  0: null,                  // none (success)
  1: 'timeout',
  2: 'dns',
  3: 'connection',          // generic fallback
  4: 'http_error',
  5: 'unknown',
  6: 'connection_refused',
  7: 'connection_reset',
  8: 'host_unreachable',
  9: 'tls_error',
};

export function regionLabel(code: string): string {
  return REGION_LABELS[code] ? `${REGION_LABELS[code]} (${code})` : code;
}

function escapeLabelValue(v: string): string {
  return v.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export function useProbeMetrics(
  tenantId: string | null | undefined,
  scenarioId: string | null | undefined,
  refreshIntervalMs = 15_000,
  paused = false,
) {
  const [targets, setTargets] = useState<ProbeTargetStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const intervalMs = useAdaptivePolling({
    activeMs: refreshIntervalMs,
    idleMs: Math.max(60_000, refreshIntervalMs * 4),
  });

  const fetch = useCallback(async () => {
    if (!tenantId || !scenarioId) {
      setTargets([]);
      return;
    }

    const tid = escapeLabelValue(tenantId);
    const sid = escapeLabelValue(scenarioId);
    const selector = `{tenant_id="${tid}",scenario_id="${sid}"}`;

    // OTel Prometheus exporter may append unit suffixes; try both names
    const durationQuery   = `(probe_http_duration_ms${selector} or probe_http_duration_ms_milliseconds${selector})`;
    const statusCodeQuery = `(probe_http_status_code${selector} or probe_http_status_code_ratio${selector})`;
    const successQuery    = `(probe_http_success${selector} or probe_http_success_ratio${selector})`;
    const errorTypeQuery  = `probe_http_error_type${selector}`;

    try {
      const batch = await queryPrometheusInstantBatch([
        durationQuery,
        statusCodeQuery,
        successQuery,
        errorTypeQuery,
      ]);

      if (!mountedRef.current) return;

      const parseVector = (data: any): Array<{ metric: Record<string, string>; value: [number, string] }> =>
        data?.data?.result ?? [];

      type Entry = {
        durationMs?: number;
        statusCode?: number;
        success?: boolean;
        errorType?: string | null;
        ts: number;
      };
      const byKey = new Map<string, Entry>();

      const key = (target: string, region: string) => `${target}||${region}`;

      for (const entry of parseVector(batch[durationQuery])) {
        const r = entry.metric?.region ?? 'unknown';
        const t = entry.metric?.target ?? '';
        const ts = Array.isArray(entry.value) ? (entry.value[0] as number) * 1000 : Date.now();
        const k = key(t, r);
        byKey.set(k, { ...(byKey.get(k) ?? { ts }), durationMs: parseFloat(entry.value[1]), ts });
      }
      for (const entry of parseVector(batch[statusCodeQuery])) {
        const r = entry.metric?.region ?? 'unknown';
        const t = entry.metric?.target ?? '';
        const k = key(t, r);
        byKey.set(k, { ...(byKey.get(k) ?? { ts: Date.now() }), statusCode: parseInt(entry.value[1], 10) });
      }
      for (const entry of parseVector(batch[successQuery])) {
        const r = entry.metric?.region ?? 'unknown';
        const t = entry.metric?.target ?? '';
        const k = key(t, r);
        byKey.set(k, { ...(byKey.get(k) ?? { ts: Date.now() }), success: parseFloat(entry.value[1]) >= 1 });
      }
      for (const entry of parseVector(batch[errorTypeQuery])) {
        const r = entry.metric?.region ?? 'unknown';
        const t = entry.metric?.target ?? '';
        const k = key(t, r);
        const code = parseInt(entry.value[1], 10);
        const errorType = ERROR_TYPE_BY_CODE[code] ?? 'unknown';
        byKey.set(k, { ...(byKey.get(k) ?? { ts: Date.now() }), errorType });
      }

      // Group by target
      const targetMap = new Map<string, ProbeRegionStatus[]>();
      byKey.forEach((entry, k) => {
        const [target, region] = k.split('||');
        if (!targetMap.has(target)) targetMap.set(target, []);
        targetMap.get(target)!.push({
          region,
          regionLabel: regionLabel(region),
          success: entry.success ?? false,
          statusCode: entry.statusCode ?? null,
          durationMs: entry.durationMs != null ? Math.round(entry.durationMs) : null,
          errorType: entry.errorType ?? null,
          target,
          checkedAt: new Date(entry.ts).toISOString(),
        });
      });

      const result: ProbeTargetStatus[] = [];
      targetMap.forEach((regions, target) => {
        const upCount = regions.filter((r) => r.success && (r.statusCode == null || r.statusCode < 400)).length;
        const withMs  = regions.filter((r) => r.durationMs != null);
        const avgMs   = withMs.length > 0
          ? Math.round(withMs.reduce((s, r) => s + r.durationMs!, 0) / withMs.length)
          : null;
        result.push({ target, regions, upCount, totalCount: regions.length, avgMs });
      });

      setTargets(result);
      setError(null);
    } catch (err: any) {
      if (!mountedRef.current) return;
      setError(err?.message ?? 'Failed to load probe metrics');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [tenantId, scenarioId]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    fetch();
    return () => { mountedRef.current = false; };
  }, [fetch]);

  // Polling — stopped when paused (last data stays visible)
  useEffect(() => {
    if (!tenantId || !scenarioId || paused) return;
    const id = window.setInterval(fetch, intervalMs);
    return () => window.clearInterval(id);
  }, [fetch, intervalMs, tenantId, scenarioId, paused]);

  // Flat list of all regions (backwards-compat for world map)
  const regions: ProbeRegionStatus[] = targets.flatMap((t) => t.regions);

  return { targets, regions, loading, error };
}
